<template>
  <div class="npc-editors" :data-testid="`${npcLabel(npc)}-dialogue-editor-container`">
    <div class="old-globals two-column-grid">
      <label for="voice">Voice</label>
      <voice-selector
        :voice-key="npc ? npc.voiceKey : null"
        :voices="voices"
        :readonly="readonly"
        :show-label="false"
        aria-label="NPC Voice Selector"
        data-testid="voice-selector"
        :disabled-voice-keys="disabledForNpc().map(info => info.voiceKey)"
        @update="updateNpcVoice"
      />
      <label v-if="activeTab === DialogueType.AI_NPC_PROMPT || activeTab === DialogueType.AI_NPC_DIALOGUE" for="npc-role-input">
        Role
      </label>
      <div v-if="activeTab === DialogueType.AI_NPC_PROMPT || activeTab === DialogueType.AI_NPC_DIALOGUE" class="role-field">
        <autocomplete
          input-id="npc-role-input"
          aria-label="NPC Role"
          data-testid="role-input"
          :value="npc.role"
          :results="roleResults"
          :show-add-button="false"
          :show-search-icon="false"
          :class="{'error': roleErrors.length}"
          :maxlength="30"
          :disabled="readonly"
          @search="updateRole"
          @selectResult="selectRole"
        />
        <help-tooltip v-if="roleErrors && roleErrors.length" type="error">
          <span v-for="error in roleErrors" :key="error.message">
            {{ error.message }}
            <br>
          </span>
        </help-tooltip>
        <help-tooltip v-else-if="roleWarnings && roleWarnings.length" type="warning">
          <span v-for="warning in roleWarnings" :key="warning.message">
            {{ warning.message }}
            <br>
          </span>
        </help-tooltip>
      </div>
    </div>
    <div
      v-if="activeTab === DialogueType.AI_NPC_PROMPT"
      class="npc-prompt"
      data-testid="prompt-container"
    >
      <html-editor
        label="VIP Prompt"
        subtitle="VIP prompt should include a description of their role, details on their current circumstances, and any goals, objectives, or hinderances."
        placeholder="Write or paste your VIP prompt here"
        data-testid="prompt-editor"
        :contents="promptMarkup"
        :readonly="readonly"
        :reload="state.reloadPrompt"
        :errors="errors.filter(error => (
          error.metadata.componentId === DialogueType.AI_NPC_PROMPT &&
          error.metadata.characterKey === npc.key
        ))"
        :prevent-emojis="true"
        @update="debouncedEditPrompt"
      />
      <prompt-footer
        :case-conf="caseConf"
        prompt-type="npc"
        :readonly="readonly"
        @updateUseCurrentDate="updateUseCurrentDate"
        @replacePrompt="replacePrompt"
      />
    </div>
    <dialogue-editor
      v-if="activeTab === DialogueType.NPC_Q_AND_A"
      :key="'NPC_Q_AND_A' + npc.key"
      class="new-globals"
      data-testid="questions-and-answers-container"
      :settings="npcQuestionSettings"
      :items="npcDialogue.questions || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      :title-prefix="npcLabel(npc) + ': '"
      @update="updateQuestions"
      @playAudio="(item) => playAnswerText(item, npc.voiceKey)"
    />
    <dialogue-editor
      v-if="activeTab === DialogueType.NPC_COMMANDS"
      :key="'NPC_COMMANDS' + npc.key"
      class="new-globals"
      data-testid="commands-container"
      :settings="npcCommandSettings"
      :items="npcDialogue.commands || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      :title-prefix="npcLabel(npc) + ': '"
      @update="updateCommands"
      @playAudio="(item) => playAnswerText(item, npc.voiceKey)"
    />
    <dialogue-editor
      v-if="activeTab === DialogueType.NPC_UNSOLICITED_DIALOGUE"
      :key="'NPC_UNSOLICITED_DIALOGUE' + npc.key"
      class="new-globals"
      data-testid="unsolicited-container"
      :settings="npcUnsolicitedSettings"
      :items="npcDialogue.unsolicited || []"
      :readonly="readonly"
      :errors="errors"
      :warnings="warnings"
      :title-prefix="npcLabel(npc) + ': '"
      @update="updateUnsolicited"
      @playAudio="(item) => playAnswerText(item, npc.voiceKey)"
    />

    <div
      v-if="activeTab === DialogueType.AI_NPC_DIALOGUE"
      class="ai-directed-dialogue"
      data-testid="ai-listen-for-say-this-container"
    >
      <dialogue-editor
        :key="'NPC_AI_QUESTIONS' + npc.key"
        class="ai-questions new-globals"
        data-testid="ai-listen-for-container"
        :settings="npcAiQuestionSettings"
        :items="npcDialogue.aiQuestions || []"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        @update="updateAiQuestions"
        @playAudio="(item) => playAnswerText(item, npc.voiceKey)"
      />
      <dialogue-editor
        :key="'NPC_AI_UNSOLICITED_' + npc.key"
        class="ai-unsolicited new-globals"
        data-testid="ai-say-this-container"
        :settings="npcAiUnsolicitedSettings"
        :items="npcDialogue.aiUnsolicited || []"
        :readonly="readonly"
        :errors="errors"
        :warnings="warnings"
        @update="updateAiUnsolicited"
        @playAudio="(item) => playAnswerText(item, npc.voiceKey)"
      />
    </div>
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import Autocomplete from '~lib/common/components/Autocomplete/Autocomplete.vue';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import HtmlEditor from '~lib/common/components/HtmlEditor/HtmlEditor.vue';
import { npcForScene } from '~lib/common/utils/npc';
import { npcLabel } from '~lib/common/utils/labels';
import { AiPromptRoleSuggestions, RoleAutoCorrections } from '~lib/common/validators/npcs/npcRoles';

import VoiceSelector from '../common/VoiceSelector.vue';
import DialogueEditor from './DialogueEditor.vue';
import PromptFooter from './PromptFooter.vue';
import DialogueType from './DialogueType';

export default {
  name: 'npc-editors',
  components: {
    Autocomplete,
    VoiceSelector,
    HelpTooltip,
    HtmlEditor,
    DialogueEditor,
    PromptFooter,
  },
  props: {
    activeId: {
      type: String,
      required: true,
    },
    activeTab: {
      type: String,
      required: true,
    },
    caseConf: {
      type: Object,
      required: true,
    },
    voices: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Array,
      default: () => [],
    },
    warnings: {
      type: Array,
      default: () => [],
    },
    usedInfo: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      npcQuestionSettings: {
        type: DialogueType.NPC_Q_AND_A,
        onText: 'On-at-Start Questions',
        offText: 'Off-at-Start Questions',
        questionLabel: 'Question',
        answerLabel: 'Answer',
        newQuestionLabel: 'Add a question',
        newAnswerLabel: 'Add an answer',
      },
      npcCommandSettings: {
        type: DialogueType.NPC_COMMANDS,
        onText: 'On-at-Start Commands',
        offText: 'Off-at-Start Commands',
        onSubtitle: 'Commands need to be connected to their desired effects in Case Logic',
        questionLabel: 'Command',
        newQuestionLabel: 'Add a command',
        isAnswerDisabled: true,
      },
      npcUnsolicitedSettings: {
        type: DialogueType.NPC_UNSOLICITED_DIALOGUE,
        offText: 'Unsolicited',
        offSubtitle: 'Unsolicited needs to be triggered by an action or timer in Case Logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'VIP Unsolicited',
        newAnswerLabel: 'Add a question or statement to say'
      },
      npcAiQuestionSettings: {
        type: DialogueType.NPC_Q_AND_A,
        onText: 'AI: Listen For & Say This',
        onSubtitle: 'Have the AI listen for a specific question and respond with a specific answer',
        offText: 'AI: Say This',
        questionLabel: 'Listen For',
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newQuestionLabel: 'Add a question or statement to listen for',
        newAnswerLabel: 'Add a question or answer to be said',
        newReasonLabel: 'Add a reason for this response',
        offDisabled: true,
        isAI: true,
        fullWidth: true,
      },
      npcAiUnsolicitedSettings: {
        type: DialogueType.NPC_UNSOLICITED_DIALOGUE,
        offText: 'AI: Say This',
        offSubtitle: 'Have the AI make specific statements through scenario logic',
        onDisabled: true,
        isQuestionDisabled: true,
        answerLabel: 'Say This',
        reasonLabel: 'Reason for Response',
        newAnswerLabel: 'Add a question or statement to say',
        newReasonLabel: 'Add a reason for this response',
        isAI: true,
        fullWidth: true,
      },
      state: {
        reloadPrompt: false,
      },
      roleResults: [],
      DialogueType,
      npcLabel,
    };
  },
  computed: {
    npc() {
      return npcForScene(this.caseConf.npcs, this.activeId);
    },
    npcDialogue() {
      return get(this.npc, 'dialogue', {}) || {};
    },
    promptMarkup() {
      return get(this.npc, 'dialogue.prompt.markup.en');
    },
    promptContents() {
      return get(this.npc, 'dialogue.prompt.content.en');
    },
    roleErrors() {
      const npc = this.npc;
      return this.errors.filter(error => (
        error.metadata.componentId === 'role' &&
        error.metadata.characterKey === npc.key
      ))
    },
    roleWarnings() {
      const npc = this.npc;
      return this.warnings.filter(warning => (
        warning.metadata.componentId === 'role' &&
        warning.metadata.characterKey === npc.key
      ))
    }
  },
  watch: {
    activeId(newValue, oldValue) {
      if (newValue !== oldValue && this.activeTab === DialogueType.AI_NPC_PROMPT) {
        // Force a load of the prompt when the NPC prompt is being displayed, and the NPC being displayed has changed
        this.replacePrompt({
          markup: this.promptMarkup,
          content: this.promptContents
        });
      }
    }
  },
  created() {
    this.roleResults = this.filterRoleResults(this.npc && this.npc.role);
  },
  methods: {
    disabledForNpc() {
      return this.usedInfo.filter(info => info.key !== this.activeId);
    },
    updateNpcVoice(voiceKey) {
      if (this.npc) {
        this.npc.voiceKey = voiceKey;
        this.$emit('update', {
          npcs: this.caseConf.npcs,
        });
      }
    },
    updateRole(value) {
      const input = this.findRoleInput();
      let caret = 0;
      let removedBeforeCaret = 0;
      if (input) {
        caret = input.selectionStart;
        removedBeforeCaret = caret - value.slice(0, caret).replace(/\s+/g, '').length;
      }
      let role = value && value.replace(/\s+/g, '').toUpperCase();
      if (role && RoleAutoCorrections[role]) {
        role = RoleAutoCorrections[role];
      }
      this.npc.role = role;
      this.roleResults = this.filterRoleResults(role);
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
      if (!input) return;
      // Maintain caret position when replacements are made
      this.$nextTick(() => {
        const newPos = Math.max(0, caret - removedBeforeCaret);
        input.setSelectionRange(newPos, newPos);
      });
    },
    selectRole(result) {
      if (!result) return;
      this.npc.role = result.label;
      this.roleResults = this.filterRoleResults(result.label);
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    filterRoleResults(value) {
      const term = value || '';
      return AiPromptRoleSuggestions
        .filter(role => role.includes(term))
        .map(role => ({ id: role, label: role }));
    },
    findRoleInput() {
      return document.getElementById('npc-role-input');
    },
    editPrompt(value) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.prompt = {
        markup: {
          en: value.markup
        },
        content: {
          en: value.content
        }
      };
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    debouncedEditPrompt: debounce(function(value) {
      this.editPrompt(value);
    }, 500),
    updateUseCurrentDate(value) {
      this.$emit('update', {
        dialogue: {
          useCurrentDate: value
        }
      });
    },
    updateQuestions(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.questions = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateCommands(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.commands = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateUnsolicited(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.unsolicited = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateAiQuestions(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.aiQuestions = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    updateAiUnsolicited(items) {
      if (!this.npc.dialogue) {
        this.npc.dialogue = {};
      }
      this.npc.dialogue.aiUnsolicited = items;
      this.$emit('update', {
        npcs: this.caseConf.npcs,
      });
    },
    playAnswerText(question, withVoiceId) {
      this.$emit('playAnswerText', question, withVoiceId);
    },
    replacePrompt(value) {
      this.editPrompt(value);
      // A hacky way to get the HtmlEditor to reload its contents
      this.state.reloadPrompt = true;
      this.$nextTick(() => {
        this.state.reloadPrompt = false;
      });
    },
  }
}
</script>

<style scoped>
.npc-editors {
  height: 100%;
  padding-top: 12px;
}

.two-column-grid {
  width: 480px;
  margin-bottom: 1rem;
}

.role-field {
  display: flex;
  align-items: center;
}

.role-field .autocomplete {
  flex: 1;
}

.npc-prompt {
  display: flex;
  flex-direction: column;
  height: calc(100% - 100px);
}

.html-editor {
  flex: 1;
}

.ai-directed-dialogue {
  display: flex;
  height: calc(100% - 40px);
}

.ai-unsolicited {
  margin-left: 15px;
}

.ai-directed-dialogue .list-container {
  flex: 1;
}
</style>